import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { briefKeys } from "../queryKeys";
import { toastBriefError } from "./briefErrorToast";
import { ensureBrief, fetchBrief } from "../repository";
import type { ProjectBrief } from "../types";

/**
 * The gate every brief query waits on: the org context, its RBAC (an org
 * switch fires the permission fetch without awaiting it — the Signing page's
 * `rbacSettled`), and a chosen project.
 */
export function useBriefQueriesEnabled(projectId: number | null): boolean {
  const { isReady, currentOrganization } = useOrganizationContext();
  const authStore = useAuthStore();
  const rbacSettled =
    !currentOrganization || authStore.hasLoadedPermissions(currentOrganization.id);
  return isReady && rbacSettled && !!currentOrganization && projectId !== null;
}

/** The project's brief row, or null when none exists yet (the empty state). */
export function useBrief(projectId: number | null) {
  const enabled = useBriefQueriesEnabled(projectId);
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  const query = useQuery({
    queryKey: briefKeys.root(projectId),
    queryFn: () => fetchBrief(projectId as number),
    enabled,
  });

  // Create-or-get, called before EVERY upload and "Add existing" — not only
  // the first: the RPC is idempotent and heals a trashed Brief folder on each
  // call, so the row it returns carries the folder an upload must land in.
  // Writes straight into the root cache: the row is the whole answer.
  const ensure = useMutation({
    mutationFn: () => ensureBrief(projectId as number),
    onSuccess: (brief: ProjectBrief | null) => {
      queryClient.setQueryData(briefKeys.root(projectId), brief);
    },
    onError: (error) => toastBriefError(t.brief, error, t.brief.loadFailed),
  });

  return {
    brief: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    ensure,
  };
}
